import { css } from '@emotion/react'
import { useCallback, useEffect, useState } from 'react'
import { User } from '../../../../../types'
import { CompanyContactPerson, Status } from '../../../../../types/api'
import {
  MyLabel,
  MyLabelId,
  SourcingRoute,
} from '../../reducers/sourcingAnnotationCandidates'
import { colors } from 'plume-ui'
import { StatusSection } from '@/components/pages/company/molecules/memo/StatusSection'
import { ContactPersonSection } from '@/components/pages/company/molecules/memo/ContactPersonSection'
import { MyLabelSection } from '@/components/pages/company/molecules/memo/MyLabelSection'
import { SourcingRouteSection } from '@/components/pages/company/molecules/memo/SourcingRouteSection'
import { StatusMemoSection } from '@/components/pages/company/molecules/memo/StatusMemoSection'
import { StatusPanelHeader } from '@/components/pages/company/molecules/memo/StatusPanelHeader'
import { FirstInterviewDateSection } from '@/components/pages/company/molecules/memo/FirstInterviewDateSection'

type Props = {
  isOpen: boolean
  companyShortname: string | undefined
  myLabels: MyLabel[]
  sourcingRoutes: SourcingRoute[]
  onSelectStatus: (companyId: string, status: Status, user: User) => void
  onSelectStatusBlank: (companyId: string, user: User) => void
  onSelectContactPerson: (
    companyContactPerson: CompanyContactPerson,
    user: User
  ) => void
  onSelectContactPersonBlank: (companyId: string, user: User) => void
  onSelectMyLabel: (myLabelId: MyLabelId, companyId: string) => void
  onDeselectMyLabel: (myLabelId: MyLabelId, companyId: string) => void
  onRequestEditMyLabelModal: () => void
  onSelectFirstInterviewDate: (
    companyId: string,
    firstInterviewDate: string,
    user: User
  ) => void
  onSelectFirstInterviewDateBlank: (companyId: string, user: User) => void
  onSelectSourcingRoute: (
    companyId: string,
    sourcingRouteId: number,
    sourcingRouteName: string,
    user: User
  ) => void
  onDeselectSourcingRoute: (companyId: string) => void
  onRequestEditSourcingRouteModal: () => void
  onUpdateMemo: (companyId: string, memo: string) => void
  closePanel: () => void
}

type State = {
  companyId: string
  companyName: string
  memo?: {
    content: string
    updated: string
    lastUpdateUserId: number
    lastName: string
    firstName: string
  }
  contactPersonId?: number
  contactPeople: {
    userId: number
    imageUrl?: string
    firstName?: string
    lastName?: string
  }[]
  statusId?: number
  statuses: Status[]
  firstInterviewDate?: string
  sourcingRouteId?: number
  // sourcingRoutes: SourcingRoute[]
  myLabelIds: MyLabelId[]
  // myLabels: MyLabel[]
}

export const SourcingAnnotationPanel: React.FC<Props> = ({
  isOpen,
  companyShortname,
  myLabels,
  sourcingRoutes,
  onSelectStatus,
  onSelectStatusBlank,
  onSelectContactPerson,
  onSelectContactPersonBlank,
  onSelectMyLabel,
  onDeselectMyLabel,
  onRequestEditMyLabelModal,
  onSelectFirstInterviewDate,
  onSelectFirstInterviewDateBlank,
  onSelectSourcingRoute,
  onDeselectSourcingRoute,
  onRequestEditSourcingRouteModal,
  onUpdateMemo,
  closePanel: closePanelProps,
}) => {
  const [annotation, setAnnotation] = useState<State>({
    companyId: '',
    companyName: '',
    memo: undefined,
    contactPersonId: undefined,
    contactPeople: [],
    statusId: undefined,
    statuses: [],
    firstInterviewDate: undefined,
    sourcingRouteId: undefined,
    // sourcingRoutes: [],
    myLabelIds: [],
    // myLabels: [],
  })

  useEffect(() => {
    if (companyShortname) {
      fetch(`/api/pages/companies/${companyShortname}/sourcing_annotations`)
        .then((s) => s.json())
        .then((json) =>
          setAnnotation({ ...json, companyId: json.companyShortName })
        )
    } else {
      setAnnotation({
        companyId: '',
        companyName: '',
        memo: undefined,
        contactPersonId: undefined,
        contactPeople: [],
        statusId: undefined,
        statuses: [],
        firstInterviewDate: undefined,
        sourcingRouteId: undefined,
        // sourcingRoutes: [],
        myLabelIds: [],
        // myLabels: [],
      })
    }
  }, [companyShortname])

  const closePanel = useCallback(() => {
    closePanelProps()
  }, [closePanelProps])

  const handleSelectSourcingRoute = (
    companyId: string,
    sourcingRouteId: number,
    sourcingRouteName: string,
    user: User
  ) => {
    onSelectSourcingRoute(companyId, sourcingRouteId, sourcingRouteName, user)
    setAnnotation({
      ...annotation,
      sourcingRouteId,
    })
  }

  const handleDeselectSourcingRoute = () => {
    if (!companyShortname) return
    onDeselectSourcingRoute(companyShortname)
    setAnnotation({
      ...annotation,
      sourcingRouteId: undefined,
    })
  }

  const handleSelectMyLabel = (myLabelId: MyLabelId) => {
    if (!companyShortname) return
    onSelectMyLabel(myLabelId, companyShortname)
    setAnnotation({
      ...annotation,
      myLabelIds: [...annotation.myLabelIds, myLabelId],
    })
  }

  const handleDeselectMyLabel = (myLabelId: MyLabelId) => {
    if (!companyShortname) return
    onDeselectMyLabel(myLabelId, companyShortname)
    setAnnotation({
      ...annotation,
      myLabelIds: annotation.myLabelIds.filter((it) => it !== myLabelId),
    })
  }

  return (
    <>
      {isOpen && (
        <div
          className="cover"
          css={css`
            inset: 0px;
            position: fixed;
            background-color: transparent;
            width: 100vw;
            height: 26vh;
            z-index: 23;
          `}
          onClick={closePanel}
        ></div>
      )}
      <div
        className={`status-panel ${isOpen && 'open'}`}
        css={css`
          width: 560px;
          height: calc(100% - 55px);
          background-color: ${colors.white10};
          position: fixed;
          top: 55px;
          box-shadow: -8px 8px 16px rgba(0, 0, 0, 0.08);
          z-index: 25;
          right: -560px;
          transition: 0.5s;
          margin-top: 9px;
          &.open {
            right: 0px;
          }
        `}
        data-test-id="status-edit-panel"
      >
        <StatusPanelHeader
          companyName={annotation.companyName}
          closePanel={closePanel}
        />
        <div
          css={css`
            padding: 24px 24px 32px 24px;
            display: flex;
            flex-direction: column;
            height: calc(100vh - 64px - 48px);
          `}
        >
          <StatusSection
            shortName={annotation.companyId}
            statuses={annotation.statuses}
            selectedStatusId={annotation.statusId}
            setSelectedStatusId={(statusId) => {
              setAnnotation({
                ...annotation,
                statusId: statusId,
              })
            }}
            onSelectStatus={onSelectStatus}
            onSelectStatusBlank={onSelectStatusBlank}
          />

          <ContactPersonSection
            shortname={annotation.companyId}
            contactPeople={annotation.contactPeople}
            selectedContactPersonId={annotation.contactPersonId}
            setSelectedContactPersonId={(contactPersonId) => {
              setAnnotation({
                ...annotation,
                contactPersonId: contactPersonId,
              })
            }}
            onSelectContactPerson={onSelectContactPerson}
            onSelectContactPersonBlank={onSelectContactPersonBlank}
          />

          <MyLabelSection
            myLabelIds={annotation.myLabelIds}
            myLabels={myLabels}
            onSelectMyLabel={handleSelectMyLabel}
            onDeselectMyLabel={handleDeselectMyLabel}
            onRequestEditMyLabelModal={onRequestEditMyLabelModal}
          />

          <FirstInterviewDateSection
            shortName={annotation.companyId}
            selectedFirstInterviewDate={annotation.firstInterviewDate}
            setSelectedFirstInterviewDate={(firstInterviewDate) => {
              setAnnotation({
                ...annotation,
                firstInterviewDate: firstInterviewDate,
              })
            }}
            onSelectFirstInterviewDate={onSelectFirstInterviewDate}
            onSelectFirstInterviewDateBlank={onSelectFirstInterviewDateBlank}
          />

          <SourcingRouteSection
            shortname={annotation.companyId}
            selectedSourcingRouteId={annotation.sourcingRouteId}
            sourcingRoutes={sourcingRoutes}
            onSelectSourcingRoute={handleSelectSourcingRoute}
            onDeselectSourcingRoute={handleDeselectSourcingRoute}
            onRequestEditSourcingRouteModal={onRequestEditSourcingRouteModal}
          />

          <StatusMemoSection
            shortName={annotation.companyId}
            memo={annotation.memo}
            onUpdateMemo={onUpdateMemo}
          />
        </div>
      </div>
    </>
  )
}
